import React, { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { wyscoutMatchesAPI, GoalBuildUp, MatchBuildUpAnalysis } from '@/services/api';
import GoalCard from './MatchBuildUpPlays/GoalCard';
import LoadingStatus from '@/components/ui/LoadingStatus';

export interface TeamGoalBuildUps {
  teamId: number;
  teamName: string;
  side: 'home' | 'away';
  goals: GoalBuildUp[];
}

interface Props {
  matchId: number;
  homeTeamId: number;
  awayTeamId: number;
  homeTeamName?: string;
  awayTeamName?: string;
  eventsReady?: boolean;
  analysisData?: MatchBuildUpAnalysis;
  goalGroups?: TeamGoalBuildUps[];
  focusedGoalMinute?: number;
  focusedGoalSide?: 'scored' | 'conceded';
  focusedGoalTeamId?: number;
}

const MatchBuildUpPlays: React.FC<Props> = ({
  matchId,
  eventsReady = true,
  analysisData,
  goalGroups,
  focusedGoalMinute,
  focusedGoalSide,
  focusedGoalTeamId,
}) => {
  const controlled = analysisData !== undefined || goalGroups !== undefined;
  const [fetchedAnalysis, setFetchedAnalysis] = useState<MatchBuildUpAnalysis | null>(null);
  const [loading, setLoading] = useState(!controlled);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>(focusedGoalTeamId === undefined ? focusedGoalSide || 'scored' : String(focusedGoalTeamId));

  useEffect(() => {
    if (controlled || !eventsReady) return;

    let ignore = false;

    const fetchAnalysis = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await wyscoutMatchesAPI.getMatchBuildUpPlays(matchId);
        if (!ignore) setFetchedAnalysis(response.data);
      } catch (err: unknown) {
        if (!ignore) {
          const message = isAxiosError<{ error?: string }>(err)
            ? err.response?.data?.error || err.message
            : err instanceof Error ? err.message : null;
          setError(message || 'Failed to load build-up analysis');
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchAnalysis();

    return () => {
      ignore = true;
    };
  }, [controlled, matchId, eventsReady]);

  useEffect(() => {
    if (focusedGoalTeamId !== undefined) setActiveTab(String(focusedGoalTeamId));
    else if (focusedGoalSide) setActiveTab(focusedGoalSide);
  }, [focusedGoalSide, focusedGoalTeamId]);

  const analysis = controlled ? analysisData : fetchedAnalysis;

  if (!controlled && loading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-6 mt-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Goal Build-Up Analysis</h2>
        <LoadingStatus label="Loading goal build-up analysis" className="h-32 justify-center" />
      </div>
    );
  }

  if (!controlled && error) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-6 mt-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Goal Build-Up Analysis</h2>
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  const groups = goalGroups || (analysis ? [
    { key: 'scored', label: 'Goals Scored', goals: analysis.goalsScored || [] },
    { key: 'conceded', label: 'Goals Conceded', goals: analysis.goalsConceded || [] },
  ] : []);
  const displayGroups = groups.map(group => 'teamId' in group
    ? { key: String(group.teamId), label: group.teamName, goals: group.goals }
    : group);

  if (displayGroups.length === 0) return null;

  if (displayGroups.every(group => group.goals.length === 0)) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-6 mt-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Goal Build-Up Analysis</h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm">No goals in this match to analyze.</p>
      </div>
    );
  }

  const activeGroup = displayGroups.find(group => group.key === activeTab) || displayGroups[0];
  const goals = activeGroup.goals;
  const focusedGoalMatches = (focusedGoalSide === activeTab || String(focusedGoalTeamId) === activeTab)
    && focusedGoalMinute !== undefined
    && goals.some(goal => goal.minute === focusedGoalMinute);

  return (
    <div className={`bg-white dark:bg-gray-900 rounded-lg shadow ${controlled ? 'p-4 sm:p-6' : 'p-6 mt-6'}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Goal Build-Up Analysis
        </h2>

        <div className="flex items-center gap-1">
          {displayGroups.map(group => (
            <button
              type="button"
              key={group.key}
              onClick={() => setActiveTab(group.key)}
              aria-pressed={activeGroup.key === group.key}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                activeGroup.key === group.key
                  ? group.key === 'scored' ? 'bg-green-600 text-white' : group.key === 'conceded' ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {group.label} ({group.goals.length})
            </button>
          ))}
        </div>
      </div>

      {/* Goals list */}
      <div className="space-y-4">
        {goals.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-8">
            No goals for {activeGroup.label.toLowerCase()} in this match.
          </p>
        ) : (
          goals.map((goal, idx) => (
            <GoalCard
              key={`${goal.minute}-${idx}`}
              goal={goal}
              index={idx}
              initiallyExpanded={focusedGoalMatches ? goal.minute === focusedGoalMinute : undefined}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default MatchBuildUpPlays;
